import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import {
  describeRequestSummary,
  formatLogTime,
  getSession,
  getUserSessions,
  parseLogTimestamp,
} from '../api/logsApi';
import { SessionDetail, SessionRequest, UserSession } from '../types/logs';
import { EmptyState, ErrorBanner, Modal, Skeleton, TableSkeleton } from './ui';
import { IconChevronDown } from './icons';

/* ---------- shared badges (also used by the API Logs page) ---------- */

const METHOD_STYLES: Record<string, string> = {
  GET: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  POST: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  PUT: 'bg-yellow-500/15 text-yellow-500 border-yellow-500/30',
  PATCH: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  DELETE: 'bg-red-500/15 text-red-400 border-red-500/30',
};

export function MethodBadge({ method }: { method: string }) {
  const style = METHOD_STYLES[method] ?? 'bg-edge/60 text-sub border-edge';
  return (
    <span
      className={`inline-flex min-w-[3.5rem] justify-center rounded-md border px-1.5 py-0.5 font-mono text-[11px] font-semibold ${style}`}
    >
      {method}
    </span>
  );
}

// 2xx green, 3xx blue, 4xx orange (someone asked for the wrong thing), 5xx red
// (we broke). The 4xx/5xx split is the whole point — one is a client mistake,
// the other is an outage.
export function statusStyle(status: number): string {
  if (status >= 500) return 'bg-red-500/15 text-red-400 border-red-500/30';
  if (status >= 400) return 'bg-orange-500/15 text-orange-400 border-orange-500/30';
  if (status >= 300) return 'bg-blue-500/15 text-blue-400 border-blue-500/30';
  return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
}

export function StatusBadge({ status }: { status: number }) {
  return (
    <span
      className={`inline-flex min-w-[2.75rem] justify-center rounded-md border px-1.5 py-0.5 font-mono text-[11px] font-semibold ${statusStyle(
        status
      )}`}
    >
      {status}
    </span>
  );
}

/* ---------- helpers ---------- */

function shortSession(id: string): string {
  return `${id.slice(0, 8)}…`;
}

/**
 * Which request in the timeline actually performed the stitch?
 *
 * Not "the first row carrying a user_id" — the backfill rewrites user_id onto
 * the anonymous rows BEHIND the login, so that test would point at whatever the
 * visitor did first, which is precisely the opposite of the moment we want to
 * mark.
 *
 * The session's stitched_at is stamped microseconds after the stitching
 * request's own log row is written, so the right row is simply the one closest
 * to it in time. The tolerance absorbs the second-level rounding of both
 * timestamps; -1 means "not in this list", so nothing is marked.
 */
export function findStitchIndex(requests: SessionRequest[], stitchedAt: string | null): number {
  if (!stitchedAt) return -1;
  const target = parseLogTimestamp(stitchedAt)?.getTime();
  if (!target) return -1;

  let best = -1;
  let bestDelta = Infinity;
  requests.forEach((r, i) => {
    const t = parseLogTimestamp(r.timestamp)?.getTime();
    if (t === undefined) return;
    const delta = Math.abs(t - target);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = i;
    }
  });

  return bestDelta <= 5000 ? best : -1;
}

/* ---------- timeline ---------- */

/** The request-by-request story of one session, oldest first. */
export function SessionTimeline({ sessionId }: { sessionId: string }) {
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDetail(await getSession(sessionId));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <TableSkeleton rows={4} />;
  if (error) {
    return (
      <div className="p-4">
        <ErrorBanner message={error} onRetry={load} />
      </div>
    );
  }
  if (!detail || detail.requests.length === 0) {
    return (
      <EmptyState
        message="No requests recorded for this session"
        hint="Logs older than 30 days are pruned."
      />
    );
  }

  const stitchIndex = findStitchIndex(detail.requests, detail.session?.stitched_at ?? null);

  return (
    <div className="divide-y divide-edge/60">
      {detail.requests.map((r, i) => {
        const summary = r.status >= 400 ? describeRequestSummary(r.request_summary) : null;
        return (
          <div key={r.id} className="px-4 py-2.5">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="w-32 shrink-0 tabular-nums text-sub">
                {formatLogTime(r.timestamp)}
              </span>
              <MethodBadge method={r.method} />
              <span className="min-w-0 flex-1 truncate font-mono" title={r.path}>
                {r.path_normalized}
              </span>
              <StatusBadge status={r.status} />
              <span className="w-16 shrink-0 text-right tabular-nums text-sub">
                {r.duration_ms}ms
              </span>
            </div>

            {/* The exact request where an anonymous visitor became a known user. */}
            {i === stitchIndex && (
              <p className="mt-1 pl-[8.5rem] text-xs font-semibold text-gold">✓ Logged in here</p>
            )}

            {r.response_error && (
              <p className="mt-1 break-all pl-[8.5rem] text-[11px] text-bad">
                ↳ Error: {r.response_error}
              </p>
            )}

            {/* Key names only — the gateway never stored the values. */}
            {summary && (
              <p className="mt-0.5 break-all pl-[8.5rem] text-[11px] text-sub/70">
                ↳ Request: {summary}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- modal wrapper (used from the Errors tab) ---------- */

export function SessionModal({
  sessionId,
  onClose,
}: {
  sessionId: string | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open={!!sessionId}
      title={sessionId ? `Session ${shortSession(sessionId)}` : 'Session'}
      onClose={onClose}
      size="xl"
      bodyClassName="!p-0"
    >
      {sessionId && <SessionTimeline sessionId={sessionId} />}
    </Modal>
  );
}

/* ---------- per-user journey ---------- */

function SessionCard({ session }: { session: UserSession }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border border-edge">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 text-left transition-colors hover:bg-edge/20"
      >
        <IconChevronDown
          className={`h-4 w-4 shrink-0 text-sub transition-transform ${open ? 'rotate-180' : ''}`}
        />
        <span className="font-mono text-xs" title={session.session_id}>
          {shortSession(session.session_id)}
        </span>
        <span className="text-xs text-sub">
          {formatLogTime(session.first_seen)} → {formatLogTime(session.last_seen)}
        </span>

        <span className="ml-auto flex items-center gap-2">
          <span className="text-xs text-sub">
            {session.request_count.toLocaleString('en-IN')} request
            {session.request_count === 1 ? '' : 's'}
          </span>
          {session.error_count > 0 && (
            <span className="rounded-full bg-bad/15 px-2 py-0.5 text-[11px] font-semibold text-bad">
              {session.error_count} error{session.error_count === 1 ? '' : 's'}
            </span>
          )}
          {/* Marks a session that began before this person signed in — the
              anonymous browsing that was later attributed to them. */}
          {session.stitched_at && (
            <span
              className="rounded-full border border-gold/40 bg-gold/10 px-2 py-0.5 text-[11px] font-semibold text-gold"
              title={`Anonymous session linked to this user at ${formatLogTime(session.stitched_at)}`}
            >
              ✓ stitched
            </span>
          )}
        </span>
      </button>

      {open && (
        <div className="border-t border-edge">
          <SessionTimeline sessionId={session.session_id} />
        </div>
      )}
    </div>
  );
}

/**
 * "Journey" — every session belonging to one user, newest first, each
 * expandable into its full request timeline.
 */
export function UserJourney({ userId }: { userId: string }) {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getUserSessions(userId);
      setSessions(res.sessions);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }
  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (sessions.length === 0) {
    return (
      <EmptyState
        message="No sessions recorded"
        hint="Sessions appear once this user browses with session tracking enabled. Logs are kept for 30 days."
      />
    );
  }

  return (
    <div className="space-y-2">
      {sessions.map((s) => (
        <SessionCard key={s.session_id} session={s} />
      ))}
    </div>
  );
}
